// Add recommendations without leaving the current page.
class MellowRecommendationPurchase extends HTMLElement {
  pending = false;
  successTimeout;

  connectedCallback() {
    this.addEventListener('change', this.handleVariantChange);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('submit', this.handleSubmit);
    this.syncVariant();
  }

  disconnectedCallback() {
    clearTimeout(this.successTimeout);
    this.removeEventListener('change', this.handleVariantChange);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('submit', this.handleSubmit);
  }

  handleClick = (event) => {
    if (!event.target.closest('[data-recommendation-add]')) return;

    event.preventDefault();
    event.stopPropagation();
    this.addToCart();
  };

  handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.addToCart();
  };

  async addToCart() {
    const button = this.querySelector('[data-recommendation-add]');
    const form = this.querySelector('form');

    if (
      this.pending ||
      !button ||
      button.disabled ||
      !form?.reportValidity()
    ) {
      return;
    }

    const select = this.querySelector('[data-recommendation-variant]');
    const error = this.querySelector('[ref="addToCartTextError"]');
    const liveRegion = this.querySelector('[ref="liveRegion"]');

    clearTimeout(this.successTimeout);

    if (liveRegion) {
      liveRegion.textContent = '';
    }

    const originalLabel = button.textContent;
    const body = new FormData(form);
    const sectionIds = new Set(['cart-drawer-section']);

    document
      .querySelectorAll('cart-items-component[data-section-id]')
      .forEach((element) => {
        sectionIds.add(element.dataset.sectionId);
      });

    body.set('sections', [...sectionIds].slice(0, 5).join(','));
    body.set('sections_url', window.location.pathname);

    this.pending = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.addingLabel || 'Adding…';

    if (select) {
      select.disabled = true;
    }

    if (error) {
      error.textContent = '';
      error.classList.add('hidden');
    }

    try {
      const url = new URL(form.action, window.location.href);

      if (!url.pathname.endsWith('.js')) {
        url.pathname += '.js';
      }

      const response = await fetch(url, {
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(
          result.description ||
          result.message ||
          this.dataset.errorMessage
        );
      }

      // A refresh failure must not mark a successful addition as failed.
      let cart;

      try {
        const cartResponse = await fetch(
          `${this.dataset.cartUrl || '/cart'}.js`,
          {
            headers: {
              Accept: 'application/json',
            },
          }
        );

        if (cartResponse.ok) {
          cart = await cartResponse.json();
        }
      } catch {
        // The theme cart bridge can fetch the cart again.
      }

      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: form.id,
            data: {
              source: 'mellow-recommendation-purchase',
              productId:
                this.querySelector('[data-product-id]')?.dataset.productId,
              variantId: String(body.get('id')),
              itemCount: cart?.item_count,
              sections: result.sections,
              didError: false,
            },
          },
        })
      );

      if (liveRegion) {
        liveRegion.textContent = 'Successfully added';

        this.successTimeout = setTimeout(() => {
          liveRegion.textContent = '';
        }, 3000);
      }
    } catch (exception) {
      const message =
        exception.message ||
        this.dataset.errorMessage ||
        'Unable to add this item. Please try again.';

      if (error) {
        error.textContent = message;
        error.classList.remove('hidden');
      }
    } finally {
      this.pending = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = originalLabel;

      if (select) {
        select.disabled = false;
      }
    }
  }

  handleVariantChange = (event) => {
    if (event.target.matches('[data-recommendation-variant]')) {
      this.syncVariant();
    }
  };

  syncVariant() {
    const select = this.querySelector('[data-recommendation-variant]');

    if (!select) return;

    const option = select.selectedOptions[0];

    if (!option) return;

    this.querySelector('input[name="id"]').value = option.value;
    this.querySelector('input[name="quantity"]').value =
      option.dataset.quantity || '1';

    const template = this.querySelector(
      `template[data-recommendation-price-for="${option.value}"]`
    );

    if (template) {
      this.querySelector('[data-recommendation-price]').replaceChildren(
        template.content.cloneNode(true)
      );
    }
  }
}

if (!customElements.get('mellow-recommendation-purchase')) {
  customElements.define(
    'mellow-recommendation-purchase',
    MellowRecommendationPurchase
  );
}